/**
 * Khử màu inline trung tính trong HTML do người dùng/admin soạn (Quill).
 *
 * Vấn đề: bài viết cũ hay có `style="color: #000"` hoặc nền trắng viết cứng.
 * Ở chế độ tối thành chữ đen trên nền đen — bài coi như mất.
 *
 * Luật (đã chốt với chủ dự án):
 * - Màu CÓ SẮC (đỏ, cam, xanh...) → GIỮ. Đó là chủ ý người viết.
 * - Màu trung tính đen/trắng/xám → BỎ thuộc tính, để chữ ăn theo theme.
 *
 * Chỉ XOÁ thuộc tính style, không thêm HTML nào → không mở đường XSS.
 * Ở chế độ sáng không đụng gì (bài giữ nguyên như cũ).
 */

/** Đọc màu CSS bất kỳ ra [r,g,b]; trả null nếu không phân giải được */
function parseColor(value) {
    if (!value) return null;

    const text = String(value).trim().toLowerCase();
    if (!text || text === 'inherit' || text === 'transparent' || text === 'currentcolor') {
        return null;
    }

    const rgb = text.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/);
    if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];

    const hex = text.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
    if (hex) {
        let h = hex[1];
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        return [
            parseInt(h.slice(0, 2), 16),
            parseInt(h.slice(2, 4), 16),
            parseInt(h.slice(4, 6), 16),
        ];
    }

    if (text === 'black') return [0, 0, 0];
    if (text === 'white') return [255, 255, 255];
    return null;
}

/** Độ bão hoà theo mô hình HSL, 0 = xám tuyệt đối */
function saturation([r, g, b]) {
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    if (max === min) return 0;
    const lightness = (max + min) / 2;
    return lightness > 0.5
        ? (max - min) / (2 - max - min)
        : (max - min) / (max + min);
}

/** Độ sáng tương đối, xấp xỉ đủ dùng để phân biệt sáng/tối */
function luminance([r, g, b]) {
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/** Ngưỡng dưới mức này coi là màu xám/trung tính, không phải chủ ý phối màu */
const NEUTRAL_SATURATION = 0.15;

/**
 * Duyệt các phần tử con có style inline và gỡ màu trung tính.
 * @param {HTMLElement|null} root phần tử chứa nội dung bài
 * @param {boolean} isDark chỉ xử lý khi đang ở chế độ tối
 */
export default function neutralizeInlineColors(root, isDark) {
    if (!root || !isDark) return;

    root.querySelectorAll('[style]').forEach((el) => {
        const textColor = parseColor(el.style.color);
        // Chữ tối và không có sắc → bỏ, để ăn theo màu chữ của theme
        if (textColor && saturation(textColor) < NEUTRAL_SATURATION && luminance(textColor) < 0.45) {
            el.style.removeProperty('color');
        }

        const bgColor = parseColor(el.style.backgroundColor);
        // Nền sáng và không có sắc → bỏ, để lộ nền tối của theme
        if (bgColor && saturation(bgColor) < NEUTRAL_SATURATION && luminance(bgColor) > 0.75) {
            el.style.removeProperty('background-color');
        }
    });
}
